import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { MessagesSquare, Users } from 'lucide-react'
import { createGroupChat, sendGroupMessage } from '@/utils/groupChatQueryOptions'
import type { CreateGroupChatRequest } from '@/types/groupChatTypes'
import type { Group } from '@/types/groupTypes'
import { RecipientField } from './RecipientField'
import { UnreachableNotice } from './UnreachableNotice'
import { PoolFullHint } from './NumberPoolDialog'
import { TemplatePicker } from './TemplatePicker'
import { useRecipientChips } from './useRecipientChips'
import { insertTemplateBody, unfilledPlaceholders } from '@/lib/format'
import { canReceiveSMS } from '@/lib/phone'
import { useShellStore } from '@/stores/shellStore'
import { useUserStore } from '@/stores/userStore'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'

export function NewChatDialog() {
  const open = useShellStore((s) => s.newChatOpen)
  const close = useShellStore((s) => s.closeNewChat)
  const selectConversation = useShellStore((s) => s.selectConversation)
  const queryClient = useQueryClient()
  const user = useUserStore((s) => s.User)
  const isSuperAdmin = user?.isSuperAdmin ?? false

  const field = useRecipientChips()
  const [name, setName] = useState('')
  // Once the user edits the name themselves, stop auto-filling it from groups.
  const [nameTouched, setNameTouched] = useState(false)
  const [firstMessage, setFirstMessage] = useState('')
  const [error, setError] = useState<string | null>(null)
  // Set once the chat exists, so a failed first-message send retries the send
  // instead of creating a second chat.
  const [createdChatId, setCreatedChatId] = useState<string | null>(null)

  const {
    recipients,
    textable,
    unreachable,
    groupsResolving,
    groupChips,
    personChips,
    hasChips,
    recipientLine,
  } = field

  // Judged the same way the count above the button is judged, so "create chat
  // with 5" always means these five. A number too mangled to dial would
  // otherwise be added as a participant and fail at Twilio.
  const participants = recipients.filter((r) => canReceiveSMS(r.phoneNumber))

  // Adding the first group also names the chat, unless the user typed a name.
  const namedField = {
    ...field,
    addGroup: (g: Group) => {
      field.addGroup(g)
      if (!nameTouched && !name.trim()) setName(g.displayName)
    },
  }

  const reset = () => {
    field.clear()
    setName('')
    setNameTouched(false)
    setFirstMessage('')
    setError(null)
    setCreatedChatId(null)
  }

  const onOpenChange = (o: boolean) => {
    if (!o) {
      close()
      reset()
    }
  }

  const createMutation = useMutation({
    mutationFn: async () => {
      let chatId = createdChatId
      if (!chatId) {
        // A chat built from exactly one group stays linked to that group;
        // anything mixed is a custom roster.
        const singleGroup = groupChips.length === 1 && personChips.length === 0
        const request: CreateGroupChatRequest = {
          name: name.trim(),
          chatType: singleGroup ? 'azure_ad' : 'custom',
          azureAdGroupId: singleGroup ? groupChips[0].id : undefined,
          participants: participants.map((r) => ({
            phoneNumber: r.phoneNumber,
            displayName: r.displayName,
            azureAdUserId: r.azureAdUserId,
            email: r.email,
          })),
        }
        const chat = await createGroupChat(request)
        chatId = chat.id
        setCreatedChatId(chat.id)
        queryClient.invalidateQueries({ queryKey: ['groupChats'] })
      }

      const first = firstMessage.trim()
      if (first) {
        await sendGroupMessage(chatId, { message: first })
      }
      return chatId
    },
    onSuccess: (chatId) => {
      queryClient.invalidateQueries({ queryKey: ['groupChats'] })
      close()
      reset()
      selectConversation(chatId, 'chat')
    },
    onError: (e: Error) => setError(e.message),
  })

  const unfilled = unfilledPlaceholders(firstMessage)
  const canCreate =
    name.trim().length > 0 &&
    textable > 0 &&
    !groupsResolving &&
    unfilled.length === 0 &&
    !createMutation.isPending

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88vh] max-w-xl overflow-y-auto">
        {/* Header — chat bubbles + teal mark this as the shared-thread screen. */}
        <DialogHeader>
          <div className="flex items-center gap-2.5">
            <span className="flex h-9 w-9 flex-none items-center justify-center rounded-[10px] bg-teal-soft text-teal">
              <MessagesSquare className="h-[18px] w-[18px]" />
            </span>
            <div className="min-w-0">
              <DialogTitle>New group chat</DialogTitle>
              <DialogDescription className="mt-0.5 text-[12px]">
                One shared thread · everyone sees every reply
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="chatName">Chat name</Label>
            <Input
              id="chatName"
              placeholder="e.g. Storm Response"
              value={name}
              onChange={(e) => {
                setName(e.target.value)
                setNameTouched(true)
              }}
            />
          </div>

          <RecipientField
            field={namedField}
            label="Members"
            placeholder="Add groups or people…"
          />

          {/* Member summary */}
          {hasChips && (
            <div className="flex flex-wrap items-center gap-2.5">
              <span className="inline-flex items-center gap-[7px] rounded-full bg-teal-soft px-[13px] py-1.5 text-[13px] font-bold text-foreground">
                <Users className="h-[13px] w-[13px] text-teal" />
                {recipientLine}
                {groupsResolving && <span className="font-medium opacity-70">· resolving…</span>}
              </span>
              <UnreachableNotice unreachable={unreachable} />
            </div>
          )}

          <div className="space-y-1.5">
            <div className="flex items-center justify-between gap-2">
              <Label htmlFor="firstMessage">First message (optional)</Label>
              <TemplatePicker
                onSelect={(t) => setFirstMessage((prev) => insertTemplateBody(prev, t.body))}
              />
            </div>
            <Textarea
              id="firstMessage"
              placeholder="Sent to everyone as soon as the chat is created…"
              value={firstMessage}
              onChange={(e) => setFirstMessage(e.target.value)}
              className="min-h-[90px]"
            />
            {unfilled.length > 0 && (
              <p className="text-[12px] font-semibold text-danger-ink">
                Fill in {unfilled.join(', ')} before sending
              </p>
            )}
          </div>

          {error && (
            <div className="space-y-2 rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
              <p>
                {createdChatId
                  ? `The chat was created, but the first message didn’t send: ${error}`
                  : error}
              </p>
              {/* The pool being full is fixed by archiving a chat, which lives
                  behind the number-pool screen — so link straight to it. */}
              {isSuperAdmin && error.includes('group chat numbers are in use') && <PoolFullHint />}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {createdChatId ? 'Close' : 'Cancel'}
          </Button>
          <Button onClick={() => createMutation.mutate()} disabled={!canCreate}>
            {createMutation.isPending
              ? createdChatId
                ? 'Sending…'
                : 'Creating…'
              : createdChatId
                ? 'Retry message'
                : `Create chat with ${textable}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
